/**
 * Das Blatt „Stand erfassen" (Spec 46b, „Blatt Stand erfassen"): Foto-Ablage,
 * Ziffernfelder mit dem letzten Stand als grauem Platzhalter, „Abgelesen am".
 * Das Web liest Fotos nicht aus: Ein Foto ist nur Beleg (Quelle `photo`), die
 * Ziffern tippt die Person — ohne Foto ist die Quelle `manual`. Am Telefon
 * öffnet `capture="environment"` die Kamera des Browsers, am Rechner den
 * Dateidialog.
 *
 * Geprüft wird am Server. Ein 422 „Kleiner als der letzte Stand …" zeigt den
 * Einstieg „Zähler wurde getauscht" (zweites Blatt über diesem); alles andere
 * (403/409/5xx/Netz, Upload) steht als Formularfehler mit „Erneut versuchen",
 * Eingabe, Foto und Datum bleiben (Regel 21). Der Inhalt hängt im
 * `SheetContent` und beginnt dadurch bei jedem Öffnen frisch.
 */

import { Camera } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { RefObject, SubmitEvent } from 'react'

import { FIELD_CLASS, FormField } from '@/components/FormField'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import type { MeterDetail } from '@/features/energy/api'
import { DigitFields } from '@/features/energy/DigitFields'
import { MeterReplaceSheet } from '@/features/energy/MeterReplaceSheet'
import { valueOf } from '@/features/energy/reading-draft'
import { showsReplaceEntry, useReadingUpload } from '@/features/energy/useReadingUpload'
import { todayInBerlin } from '@/lib/date'

interface MeterReadingSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly meter: MeterDetail
}

/** Nach dem Speichern schließt das Blatt; Liste und Detail laden über die
 * Invalidierung von `keys.energy.all` still nach (Spec 46b, Regel 22). */
export function MeterReadingSheet({ open, onOpenChange, meter }: MeterReadingSheetProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        data-testid="meter-capture-sheet"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          inputRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[560px] md:data-[side=right]:!max-w-[560px]"
      >
        <ReadingForm
          meter={meter}
          inputRef={inputRef}
          onClose={() => {
            onOpenChange(false)
          }}
        />
      </SheetContent>
    </Sheet>
  )
}

interface Attachment {
  readonly file: File
  /** `URL.createObjectURL` — wird beim Ersetzen, Entfernen und Schließen freigegeben. */
  readonly url: string
}

interface ReadingFormProps {
  readonly meter: MeterDetail
  readonly inputRef: RefObject<HTMLInputElement | null>
  readonly onClose: () => void
}

function ReadingForm({ meter, inputRef, onClose }: ReadingFormProps) {
  const upload = useReadingUpload()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const today = todayInBerlin()
  const [typed, setTyped] = useState('')
  const [readOn, setReadOn] = useState(today)
  const [attachment, setAttachment] = useState<Attachment | null>(null)
  const [isReplaceOpen, setIsReplaceOpen] = useState(false)

  // Die Vorschau-URL gehört dem Blatt: beim Schließen freigeben.
  const attachmentUrl = useRef<string | null>(null)
  useEffect(
    () => () => {
      if (attachmentUrl.current !== null) URL.revokeObjectURL(attachmentUrl.current)
    },
    [],
  )

  function selectFile(file: File | null) {
    if (attachmentUrl.current !== null) URL.revokeObjectURL(attachmentUrl.current)
    attachmentUrl.current = file === null ? null : URL.createObjectURL(file)
    setAttachment(file === null || attachmentUrl.current === null ? null : { file, url: attachmentUrl.current })
  }

  const value = valueOf(typed)
  const { isSaving, error } = upload
  const canSave = value !== null && readOn !== '' && !isSaving
  const last = meter.last_reading
  const hint = last === null ? 'Noch kein Stand' : `Zuletzt ${last.value_display} ${meter.unit} am ${last.read_on_display}`

  async function submit() {
    if (value === null || !canSave) return
    const isSaved = await upload.save({ meterId: meter.id, value, readOn, photo: attachment?.file ?? null })
    if (!isSaved) return
    onClose()
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    void submit()
  }

  return (
    <>
    <form onSubmit={handleSubmit} className="flex h-full flex-col" aria-busy={isSaving}>
      <SheetHeader>
        <SheetTitle className="text-section font-(--font-weight-section) text-text">Stand erfassen</SheetTitle>
        <p className="text-label text-muted">
          {meter.label}
          {meter.house !== null && ` · ${meter.house.label}`}
        </p>
      </SheetHeader>

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
        <div
          data-testid="meter-capture-window"
          className={`rounded-card relative flex aspect-video w-full items-center justify-center overflow-hidden border ${
            attachment === null ? 'border-border border-dashed' : 'border-border'
          }`}
        >
          {attachment === null ? (
            <div className="text-muted flex flex-col items-center gap-2">
              <Camera aria-hidden="true" className="size-6" />
              <button
                type="button"
                disabled={isSaving}
                onClick={() => fileInputRef.current?.click()}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Foto wählen
              </button>
            </div>
          ) : (
            <>
              <img
                data-testid="meter-capture-photo"
                src={attachment.url}
                alt="Vorschau des Belegfotos"
                className="bg-bg size-full object-contain"
              />
              <button
                type="button"
                data-testid="meter-capture-photo-remove"
                disabled={isSaving}
                onClick={() => {
                  selectFile(null)
                }}
                className="border-border bg-surface text-text rounded-card text-label absolute right-2 bottom-2 min-h-(--tap-min) border px-3"
              >
                Foto entfernen
              </button>
            </>
          )}
          <input
            ref={fileInputRef}
            data-testid="meter-capture-file"
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            tabIndex={-1}
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file !== undefined) selectFile(file)
              // Dieselbe Datei erneut wählen soll wieder auslösen.
              event.target.value = ''
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <DigitFields
            digits={meter.digits}
            typed={typed}
            onTypedChange={(next) => {
              setTyped(next)
              upload.clearError()
            }}
            lastValue={last?.value ?? null}
            unit={meter.unit}
            idPrefix="meter-capture"
            containerTestId="meter-capture-digits"
            label="Zählerstand"
            inputRef={inputRef}
            disabled={isSaving}
          />
          <p data-testid="meter-capture-hint" className="text-label text-muted">
            {hint}
          </p>
          {error.field?.field === 'value' && (
            <div className="flex flex-col items-start">
              <p id="meter-capture-error-value" data-testid="meter-capture-error-value" role="alert" className="text-label text-status-overdue">
                {error.field.message}
              </p>
              {showsReplaceEntry(error) && (
                <button
                  type="button"
                  data-testid="meter-capture-replace"
                  onClick={() => {
                    setIsReplaceOpen(true)
                  }}
                  className="text-accent text-label min-h-(--tap-min) hover:underline"
                >
                  Zähler wurde getauscht
                </button>
              )}
            </div>
          )}
        </div>

        <FormField
          label="Abgelesen am"
          testId="meter-capture-read-on"
          apiField="read_on"
          errorPrefix="meter-capture"
          type="date"
          max={today}
          required
          disabled={isSaving}
          value={readOn}
          onChange={(next) => {
            setReadOn(next)
            upload.clearError()
          }}
          error={error.field}
        />
      </div>

      <SheetFooter>
        {error.general !== undefined && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p data-testid="meter-capture-error" role="alert" className="text-label text-status-overdue">
              Nicht gespeichert. {error.general}
            </p>
            <button
              type="button"
              data-testid="meter-capture-retry"
              onClick={() => void submit()}
              className="text-accent text-label min-h-(--tap-min) hover:underline"
            >
              Erneut versuchen
            </button>
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" data-testid="meter-capture-cancel" onClick={onClose} className={`text-text px-4 ${FIELD_CLASS}`}>
            Abbrechen
          </button>
          <button
            type="submit"
            data-testid="meter-capture-save"
            disabled={!canSave}
            aria-busy={isSaving}
            className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
          >
            Stand sichern
          </button>
        </div>
      </SheetFooter>
    </form>

    {/* Außerhalb des `<form>`: React reicht Ereignisse durch Portale weiter, das
        Absenden des Tausch-Formulars würde sonst auch „Stand sichern" auslösen. */}
    <MeterReplaceSheet
      open={isReplaceOpen}
      onOpenChange={setIsReplaceOpen}
      meter={meter}
      onReplaced={() => {
        upload.clearError()
      }}
    />
    </>
  )
}
